import { LocalizedText, useSourceLanguage } from "../i18n/LocalizedCopy";
import HomeTrustWelcome from "../components/HomeTrustWelcome";
import EternuaIcon from "../components/EternuaIcon";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import usePageSeo from "../seo/usePageSeo";

import {
  loadHomeDashboard,
  recordHomeUniverseActivity,
  resetHomePersonalization,
  sendHomeRecommendationFeedback,
  updateHomePreferences,
} from "../api/homeApi";
import { useAuth } from "../context/AuthContext";
import { setFavorite } from "../global-search/api";
import { formatWalletValue } from "../wallet/formatters";
import { useWalletSummary } from "../wallet/WalletContext";
import EternuaOrientation from "../feature-status/EternuaOrientation";
import ConciergeEternua, { ConciergeTodayActions } from "../concierge/ConciergeEternua";
import GuestHomePage from "./GuestHomePage";
import LifeToolsLinks from "../personal-life/LifeToolsLinks";
import EternuaMissionTeaser from "../components/EternuaMissionTeaser";
import "./EternuaDashboardPage.css";

const SHORTCUT_ICONS = {
  briefcase: "▣",
  market: "◇",
  service: "✦",
  outing: "◎",
  journey: "↻",
  message: "▱",
  discover: "∞",
};

const UNIVERSE_MARKS = {
  marketplace: "A",
  emploi: "E",
  services: "S",
  sorties: "O",
  crowdfunding: "C",
};

const EMPTY_DASHBOARD = {
  member: null,
  shortcuts: [],
  available_shortcuts: [],
  priorities: { results: [], count: 0 },
  recommendations: { results: [], count: 0, next: null },
  progress: null,
  discovery: { results: [], all: [] },
  preferences: null,
};

function formatDate(value, lang, t) {
  if (!value) return t("memberHome.noDate", "Date non renseignée");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t("memberHome.noDate", "Date non renseignée");
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "fr-FR", sameDay
    ? { hour: "2-digit", minute: "2-digit" }
    : { day: "numeric", month: "short", year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined }
  ).format(date);
}

function formatPrivateAppointment(value, lang, t) {
  if (!value) return t("memberHome.toCheck", "À consulter");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t("memberHome.toCheck", "À consulter");

  const today = new Date();
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysFromToday = Math.round((dateDay - todayDay) / 86_400_000);
  const hour = date.getHours();
  const minute = String(date.getMinutes()).padStart(2, "0");
  const time = lang === "en"
    ? new Intl.DateTimeFormat("en-GB", { hour: "numeric", minute: "2-digit", hour12: true }).format(date)
    : minute === "00" ? `${hour} h` : `${hour} h ${minute}`;

  if (daysFromToday === 0) return `${t("memberHome.today", "aujourd’hui")} ${time}`;
  if (daysFromToday === 1) return `${t("memberHome.tomorrow", "demain")} ${time}`;
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "short" }).format(date);
}

function DashboardSkeleton() {
  useSourceLanguage();
  const { t } = useI18n();
  return (
    <main className="home-dashboard home-dashboard--loading" aria-busy="true" aria-label={t("memberHome.loadingAria", "Chargement de ton accueil")}>
      <span className="home-sr-only">{<LocalizedText source={t("memberHome.loading", "Chargement de ton accueil personnalisé…")} />}</span>
      <div className="home-skeleton home-skeleton--hero" />
      <div className="home-skeleton home-skeleton--wide" />
      <div className="home-skeleton-grid">
        <div className="home-skeleton" />
        <div className="home-skeleton" />
        <div className="home-skeleton" />
      </div>
    </main>
  );
}

function StatusPanel({ "data-et-copy": __etCopy = "", offline, message, onRetry }) {
  useSourceLanguage();
  const { t } = useI18n();
  return (
    <main className="home-dashboard home-dashboard--status">
      <section className="home-status-card" role="alert">
        <span className="home-status-card__mark" aria-hidden="true">{offline ? "∿" : "!"}</span>
        <p className="home-eyebrow">{offline ? <LocalizedText source={t("memberHome.offline", "Hors connexion")} /> : <LocalizedText source={t("memberHome.unavailable", "Accueil indisponible")} />}</p>
        <h1>{offline ? <LocalizedText source={t("memberHome.network", "Reviens quand le réseau sera disponible")} /> : <LocalizedText source={t("memberHome.pause", "Ton espace fait une pause")} />}</h1>
        <p>{<LocalizedText source={message} marked={__etCopy.split(',').includes("message")} />}</p>
        <button type="button" className="home-button home-button--primary" onClick={onRetry}>{<LocalizedText source={t("memberHome.retry", "Réessayer")} />}</button>
      </section>
    </main>
  );
}

function HomeSectionHeading({ "data-et-copy": __etCopy = "", eyebrow, title, description, action }) {
  useSourceLanguage();
  return (
    <header className="home-section-heading">
      <div>
        {eyebrow ? <p className="home-eyebrow">{eyebrow}</p> : null}
        <h2>{<LocalizedText source={title} marked={__etCopy.split(',').includes("title")} />}</h2>
        {description ? <p>{<LocalizedText source={description} marked={__etCopy.split(',').includes("description")} />}</p> : null}
      </div>
      {action}
    </header>
  );
}

function PrioritySection({ priorities }) {
  useSourceLanguage();
  const { t, lang } = useI18n();
  const items = priorities?.results || [];
  return (
    <section className="home-section home-priorities" aria-labelledby="home-priorities-title">
      <HomeSectionHeading
        eyebrow={t("memberHome.priorities", "Tes priorités")}
        title={<span id="home-priorities-title">{<LocalizedText source={t("memberHome.todo", "À faire maintenant")} />}</span>}
        description={t("memberHome.attention", "Uniquement ce qui demande vraiment ton attention.")}
      />
      {items.length ? (
        <div className="home-priority-list">
          {items.map((item) => (
            <article className={`home-priority home-priority--${item.urgency || "normal"}`} key={item.id}>
              <div className="home-priority__rail" aria-hidden="true" />
              <div className="home-priority__copy">
                <div className="home-priority__meta">
                  <span>{item.universe}</span>
                  <time dateTime={item.date || undefined}>{formatDate(item.date, lang, t)}</time>
                </div>
                <h3>{item.title}</h3>
                <p>{item.summary}</p>
              </div>
              <div className="home-priority__action">
                <span className="home-status-pill">{item.status}</span>
                <Link to={item.action.url}>{item.action.label}<span aria-hidden="true"> →</span></Link>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="home-empty-state">
          <span aria-hidden="true">✓</span>
          <div>
            <h3>{<LocalizedText source={t("memberHome.updated", "Tout est à jour")} />}</h3>
            <p>{<LocalizedText source={t("memberHome.updatedLead", "Aucun message, rendez-vous ou suivi ne demande ton attention pour l'instant.")} />}</p>
          </div>
        </div>
      )}
    </section>
  );
}

function ShortcutsSection({ shortcuts }) {
  useSourceLanguage();
  const { t } = useI18n();
  return (
    <section className="home-shortcuts" aria-labelledby="home-shortcuts-title">
      <div className="home-shortcuts__label">
        <h2 id="home-shortcuts-title">{<LocalizedText source={t("memberHome.shortcuts", "Accès rapides")} />}</h2>
        <span>{<LocalizedText source={t("memberHome.next", "Choisis ton prochain pas")} />}</span>
      </div>
      <nav className="home-shortcut-list" aria-label={t("memberHome.shortcutsAria", "Raccourcis personnalisés")}>
        {shortcuts.map((item) => (
          <Link to={item.route} key={item.key} className="home-shortcut">
            <span aria-hidden="true">{((__etChoice) => __etChoice ? <LocalizedText source={__etChoice} /> : ("·"))(SHORTCUT_ICONS[item.icon])}</span>
            <strong>{<LocalizedText source={t(`memberHome.shortcut.${item.key}`, item.label)} />}</strong>
          </Link>
        ))}
      </nav>
    </section>
  );
}

function RecommendationCard({ item, whyOpen, onToggleWhy, onSave, onFeedback, busy }) {
  useSourceLanguage();
  const { t } = useI18n();
  const universeKey = item.universe?.key || "";
  return (
    <article className="home-recommendation">
      <div className="home-recommendation__visual">
        {item.image_url ? <img src={item.image_url} alt="" loading="lazy" /> : null}
        <span aria-hidden="true">{((__etChoice) => __etChoice ? <LocalizedText source={__etChoice} /> : ("∞"))(UNIVERSE_MARKS[universeKey])}</span>
        <small>{item.nature}</small>
      </div>
      <div className="home-recommendation__body">
        <div className="home-recommendation__meta">
          <span>{item.universe?.label || <LocalizedText source={"Eternua"} />}</span>
          {item.location ? <span>{item.location}</span> : null}
        </div>
        <h3>{item.title}</h3>
        {item.summary ? <p>{item.summary}</p> : null}
        <div className="home-recommendation__main-actions">
          <Link className="home-button home-button--small home-button--primary" to={item.url}>{<LocalizedText source={t("memberHome.view", "Voir")} />}</Link>
          {item.can_save ? (
            <button
              type="button"
              className="home-button home-button--small home-button--quiet"
              disabled={busy || item.favorite}
              onClick={() => onSave(item)}
            >
              {item.favorite ? <LocalizedText source={t("memberHome.saved", "Enregistré")} /> : <LocalizedText source={t("memberHome.save", "Enregistrer")} />}
            </button>
          ) : null}
        </div>
        <div className="home-recommendation__secondary-actions">
          <button type="button" disabled={busy} onClick={() => onFeedback(item, "hidden")}>{<LocalizedText source={t("memberHome.hide", "Masquer")} />}</button>
          <button type="button" disabled={busy} onClick={() => onFeedback(item, "less_similar")}>{<LocalizedText source={t("memberHome.less", "Voir moins de contenus similaires")} />}</button>
          <button type="button" aria-expanded={whyOpen} onClick={() => onToggleWhy(item.id)}>{<LocalizedText source={t("memberHome.why", "Pourquoi ?")} />}</button>
        </div>
        {whyOpen ? (
          <div className="home-recommendation__why">
            <strong>{item.reason?.label || <LocalizedText source={t("memberHome.whyTitle", "Pourquoi cette proposition ?")} />}</strong>
            <p>{item.reason?.detail || <LocalizedText source={t("memberHome.whyDetail", "Cette proposition utilise seulement les signaux de personnalisation que tu as autorisés.")} />}</p>
          </div>
        ) : null}
      </div>
    </article>
  );
}

function RecommendationsSection({ recommendations, preferences, onOpenSettings, onSave, onFeedback, onLoadMore, busyId, loadingMore }) {
  useSourceLanguage();
  const { t } = useI18n();
  const [whyOpen, setWhyOpen] = useState(null);
  const items = recommendations?.results || [];
  return (
    <section className="home-section home-recommendations" aria-labelledby="home-recommendations-title">
      <HomeSectionHeading
        eyebrow={t("memberHome.selection", "Sélection explicable")}
        title={<span id="home-recommendations-title">{<LocalizedText source={t("memberHome.chosen", "Choisi pour toi")} />}</span>}
        description={preferences?.personalization_enabled
          ? t("memberHome.inspired", "Inspiré de tes choix, sans donnée sensible ni score caché.")
          : t("memberHome.disabledLead", "La personnalisation est désactivée : voici uniquement des contenus récents ou populaires.")}
        action={<button type="button" className="home-text-button" onClick={onOpenSettings}>{<LocalizedText source={t("memberHome.interests", "Modifier mes centres d'intérêt")} />}</button>}
      />
      {items.length ? (
        <>
          <div className="home-recommendation-grid">
            {items.map((item) => (
              <RecommendationCard
                key={item.id}
                item={item}
                whyOpen={whyOpen === item.id}
                onToggleWhy={(id) => setWhyOpen((current) => current === id ? null : id)}
                onSave={onSave}
                onFeedback={onFeedback}
                busy={busyId === item.id}
              />
            ))}
          </div>
          {recommendations.next ? (
            <button type="button" className="home-load-more" disabled={loadingMore} onClick={onLoadMore}>
              {loadingMore ? <LocalizedText source={t("memberHome.loadingMore", "Chargement…")} /> : <LocalizedText source={t("memberHome.more", "Afficher d'autres propositions")} />}
            </button>
          ) : null}
        </>
      ) : (
        <div className="home-empty-state home-empty-state--recommendations">
          <span aria-hidden="true">∞</span>
          <div>
            <h3>{<LocalizedText source={t("memberHome.preparing", "Ta sélection se prépare")} />}</h3>
            <p>{recommendations?.unavailable_reason || <LocalizedText source={t("memberHome.noSelection", "Aucun contenu disponible ne correspond encore à tes choix.")} />}</p>
          </div>
        </div>
      )}
    </section>
  );
}

function ProgressSection({ progress, walletState }) {
  useSourceLanguage();
  const { t } = useI18n();
  const { data: wallet, status, amountsHidden, error } = walletState;
  const walletLabel = (kind, value) => formatWalletValue({
    kind,
    value,
    currency: wallet?.currency,
    hidden: amountsHidden,
    unavailableLabel: status === "loading" ? t("memberHome.loadingMore", "Chargement…") : t("memberHome.unavailableValue", "Indisponible"),
  });
  const percent = Math.max(0, Math.min(100, Number(progress?.progress_percent || 0)));
  return (
    <section className="home-section home-progress" aria-labelledby="home-progress-title">
      <HomeSectionHeading
        eyebrow={t("memberHome.personal", "Ton espace personnel")}
        title={<span id="home-progress-title">{<LocalizedText source={t("memberHome.myEternua", "Mes Eternua")} />}</span>}
        description={t("memberHome.central", "Progression, missions et portefeuille centralisés.")}
        action={<Link className="home-text-button" to="/mes-eternua">{<LocalizedText source={t("memberHome.open", "Ouvrir mon espace")} />}</Link>}
      />
      <div className="home-progress-grid">
        <article className="home-level-card">
          <div className="home-level-card__top">
            <span className="home-level-orb" aria-hidden="true">{progress?.current_badge?.icon || "✦"}</span>
            <div>
              <small>{<LocalizedText source={t("memberHome.level", "Niveau actuel")} />}</small>
              <strong>{progress?.level ? `${t("memberHome.levelPrefix", "Niveau")} ${progress.level}` : <LocalizedText source={t("memberHome.unavailableValue", "Indisponible")} />}</strong>
              {progress?.current_badge?.title ? <span>{progress.current_badge.title}</span> : null}
            </div>
          </div>
          <div className="home-progress-bar" aria-label={t("memberHome.progressAria", "{percent} % vers le niveau suivant").replace("{percent}", percent)} role="progressbar" aria-valuenow={percent} aria-valuemin="0" aria-valuemax="100">
            <span style={{ width: `${percent}%` }} />
          </div>
          <p>{progress?.points_to_next_level > 0 ? t("memberHome.pointsToNext", "{points} points avant le prochain niveau").replace("{points}", progress.points_to_next_level) : <LocalizedText source={t("memberHome.complete", "Niveau actuel complété")} />}</p>
        </article>

        <article className="home-wallet-card">
          <header>
            <div><small>{<LocalizedText source={t("memberHome.wallet", "Portefeuille central")} />}</small><strong>{<LocalizedText source={t("memberHome.synced", "Vue synchronisée")} />}</strong></div>
            <Link to="/portefeuille" aria-label={t("memberHome.openWallet", "Ouvrir mon portefeuille")}>→</Link>
          </header>
          <dl>
            <div><dt>{<LocalizedText source={t("memberHome.balance", "Solde en euros")} />}</dt><dd>{walletLabel("money", wallet?.balanceMinor)}</dd></div>
            <div><dt>{<LocalizedText source={t("memberHome.points", "Points Eternua")} />}</dt><dd>{walletLabel("points", wallet?.points)}</dd></div>
            <div><dt>{<LocalizedText source={t("memberHome.credits", "Crédits promotionnels")} />}</dt><dd>{walletLabel("money", wallet?.bonusMinor)}</dd></div>
            <div><dt>{<LocalizedText source={t("memberHome.pending", "Montant en attente")} />}</dt><dd>{walletLabel("money", wallet?.pendingMinor)}</dd></div>
          </dl>
          {error ? <p className="home-inline-warning">{<LocalizedText source={t("memberHome.walletPartial", "Certaines données du portefeuille ne sont pas disponibles.")} />}</p> : null}
          <small className="home-wallet-card__note">{<LocalizedText source={t("memberHome.pointsNote", "Les points ne sont ni des euros ni un solde retirable.")} />}</small>
        </article>

        <article className="home-missions-card">
          <header><small>{<LocalizedText source={t("memberHome.missions", "Missions prioritaires")} />}</small><Link to="/mes-eternua/missions">{<LocalizedText source={t("memberHome.all", "Tout voir")} />}</Link></header>
          {(progress?.missions || []).length ? (
            <ul>
              {progress.missions.map((mission) => (
                <li key={mission.id || mission.code}>
                  <div><strong>{mission.title}</strong><span>{mission.progress_text || `${mission.progress_percent || 0} %`}</span></div>
                  <div className="home-mini-progress" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, Number(mission.progress_percent || 0)))}%` }} /></div>
                </li>
              ))}
            </ul>
          ) : <p>{<LocalizedText source={t("memberHome.noMissions", "Aucune mission prioritaire pour le moment.")} />}</p>}
        </article>

        <article className="home-badges-card">
          <div>
            <small>{<LocalizedText source={t("memberHome.recent", "Badges récents")} />}</small>
            {(progress?.recent_badges || []).length
              ? progress.recent_badges.map((badge) => <strong key={`${badge.title}-${badge.earned_at}`}>{badge.title}</strong>)
              : <strong>{<LocalizedText source={t("memberHome.noBadges", "Aucun badge récent")} />}</strong>}
          </div>
          <div>
            <small>{<LocalizedText source={t("memberHome.validations", "Validations en attente")} />}</small>
            <strong>{Number.isFinite(Number(progress?.pending_validations)) ? progress.pending_validations : "—"}</strong>
          </div>
        </article>
      </div>
    </section>
  );
}

function DiscoverySection({ discovery, onVisit, onLater, onOpenAll }) {
  useSourceLanguage();
  const { t } = useI18n();
  const items = discovery?.results || [];
  return (
    <section className="home-section home-discovery" aria-labelledby="home-discovery-title">
      <HomeSectionHeading
        eyebrow={t("memberHome.pace", "Sans te presser")}
        title={<span id="home-discovery-title">{<LocalizedText source={t("memberHome.explore", "Explore Eternua à ton rythme")} />}</span>}
        description={t("memberHome.exploreLead", "Quelques univers encore peu visités, jamais toute la plateforme d'un seul coup.")}
        action={<button type="button" className="home-text-button" onClick={onOpenAll}>{<LocalizedText source={t("memberHome.universes", "Voir tous les univers")} />}</button>}
      />
      <div className="home-discovery-grid">
        {items.map((item) => (
          <article className="home-discovery-card" key={item.key}>
            <div className="home-discovery-card__image">
              <img src={item.image_url} alt="" loading="lazy" />
              <span aria-hidden="true">∞</span>
            </div>
            <div className="home-discovery-card__body">
              <h3>{<LocalizedText source={t(`memberHome.universe.${item.key}.label`, item.label)} />}</h3>
              <p>{<LocalizedText source={t(`memberHome.universe.${item.key}.description`, item.description)} />}</p>
              <div>
                <button type="button" className="home-button home-button--small home-button--primary" onClick={() => onVisit(item)}>{<LocalizedText source={t("memberHome.discover", "Découvrir")} />}</button>
                <button type="button" className="home-later-button" onClick={() => onLater(item)}>{<LocalizedText source={t("memberHome.later", "Plus tard")} />}</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ModalDialog({ "data-et-copy": __etCopy = "", title, onClose, children, className = "" }) {
  useSourceLanguage();
  const { t } = useI18n();
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (node && !node.open) node.showModal();
    return () => {
      if (node?.open) node.close();
    };
  }, []);
  return (
    <dialog ref={ref} className={`home-dialog ${className}`.trim()} aria-labelledby="home-dialog-title" onCancel={(event) => { event.preventDefault(); onClose(); }}>
      <div className="home-dialog__header">
        <h2 id="home-dialog-title">{<LocalizedText source={title} marked={__etCopy.split(',').includes("title")} />}</h2>
        <button type="button" aria-label={t("memberHome.close", "Fermer")} onClick={onClose}>×</button>
      </div>
      {children}
    </dialog>
  );
}

function PreferencesDialog({ preferences, shortcutOptions, universeOptions, busy, onClose, onSave, onReset }) {
  useSourceLanguage();
  const { t } = useI18n();
  const [draft, setDraft] = useState(() => ({
    personalization_enabled: preferences?.personalization_enabled !== false,
    navigation_history_enabled: Boolean(preferences?.navigation_history_enabled),
    interests: [...(preferences?.interests || [])],
    shortcut_keys: [...(preferences?.shortcut_keys || [])],
  }));
  const toggleList = (field, key) => setDraft((current) => ({
    ...current,
    [field]: current[field].includes(key)
      ? current[field].filter((item) => item !== key)
      : [...current[field], key],
  }));
  return (
    <ModalDialog title={t("memberHome.settings", "Personnaliser mon accueil")} onClose={onClose} className="home-dialog--settings">
      <form onSubmit={(event) => { event.preventDefault(); onSave(draft); }}>
        <section className="home-dialog__section">
          <label className="home-switch-row">
            <span><strong>{<LocalizedText source={t("memberHome.personalized", "Recommandations personnalisées")} />}</strong><small>{<LocalizedText source={t("memberHome.personalizedLead", "Utilise uniquement tes choix autorisés et tes favoris.")} />}</small></span>
            <input type="checkbox" checked={draft.personalization_enabled} onChange={(event) => setDraft((current) => ({ ...current, personalization_enabled: event.target.checked }))} />
          </label>
          <label className="home-switch-row">
            <span><strong>{<LocalizedText source={t("memberHome.history", "Historique de navigation autorisé")} />}</strong><small>{<LocalizedText source={t("memberHome.historyLead", "Mémorise seulement les univers visités pour mieux doser la découverte.")} />}</small></span>
            <input type="checkbox" checked={draft.navigation_history_enabled} onChange={(event) => setDraft((current) => ({ ...current, navigation_history_enabled: event.target.checked }))} />
          </label>
        </section>
        <fieldset className="home-dialog__section">
          <legend>{<LocalizedText source={t("memberHome.myInterests", "Mes centres d'intérêt")} />}</legend>
          <p>{<LocalizedText source={t("memberHome.myInterestsLead", "Choisis les univers que tu souhaites voir davantage. Aucun sujet sensible n'est proposé.")} />}</p>
          <div className="home-choice-grid">
            {universeOptions.map((item) => (
              <label key={item.key}><input type="checkbox" checked={draft.interests.includes(item.key)} onChange={() => toggleList("interests", item.key)} /><span>{<LocalizedText source={t(`memberHome.universe.${item.key}.label`, item.label)} />}</span></label>
            ))}
          </div>
        </fieldset>
        <fieldset className="home-dialog__section">
          <legend>{<LocalizedText source={t("memberHome.myShortcuts", "Mes raccourcis")} />}</legend>
          <div className="home-choice-grid home-choice-grid--shortcuts">
            {shortcutOptions.map((item) => (
              <label key={item.key}><input type="checkbox" checked={draft.shortcut_keys.includes(item.key)} onChange={() => toggleList("shortcut_keys", item.key)} /><span>{<LocalizedText source={t(`memberHome.shortcut.${item.key}`, item.label)} />}</span></label>
            ))}
          </div>
        </fieldset>
        <div className="home-dialog__footer">
          <button type="button" className="home-reset-button" disabled={busy} onClick={onReset}>{<LocalizedText source={t("memberHome.reset", "Réinitialiser la personnalisation")} />}</button>
          <div><button type="button" className="home-button home-button--quiet" onClick={onClose}>{<LocalizedText source={t("memberHome.cancel", "Annuler")} />}</button><button type="submit" className="home-button home-button--primary" disabled={busy}>{busy ? <LocalizedText source={t("memberHome.saving", "Enregistrement…")} /> : <LocalizedText source={t("memberHome.save", "Enregistrer")} />}</button></div>
        </div>
      </form>
    </ModalDialog>
  );
}

function AllUniversesDialog({ universes, onClose, onVisit }) {
  useSourceLanguage();
  const { t } = useI18n();
  return (
    <ModalDialog title={t("memberHome.allUniverses", "Tous les univers Eternua")} onClose={onClose} className="home-dialog--universes">
      <p className="home-dialog__lead">{<LocalizedText source={t("memberHome.allUniversesLead", "Ouvre uniquement l'espace qui t'est utile aujourd'hui.")} />}</p>
      <div className="home-all-universes">
        {universes.map((item) => (
          <button type="button" key={item.key} onClick={() => onVisit(item)}>
            <span aria-hidden="true">∞</span><span><strong>{<LocalizedText source={t(`memberHome.universe.${item.key}.label`, item.label)} />}</strong><small>{<LocalizedText source={t(`memberHome.universe.${item.key}.description`, item.description)} />}</small></span><i aria-hidden="true">→</i>
          </button>
        ))}
      </div>
    </ModalDialog>
  );
}

export default function EternuaDashboardPage() {
  useSourceLanguage();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const walletState = useWalletSummary();
  usePageSeo(user ? {
    title: t("memberHome.pageTitle", "Mon accueil — Eternua"),
    description: t("memberHome.pageDescription", "Ton espace privé : priorités, recommandations et activité Eternua."),
    indexable: false,
    shareable: false,
    robots: "noindex, nofollow, noarchive",
  } : undefined);
  const [state, setState] = useState({ status: "idle", data: EMPTY_DASHBOARD, error: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [allUniversesOpen, setAllUniversesOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [busyRecommendation, setBusyRecommendation] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const refresh = useCallback(async (signal) => {
    if (!user) return;
    setState((current) => ({ ...current, status: current.data?.member ? "refreshing" : "loading", error: null }));
    try {
      const payload = await loadHomeDashboard({ signal });
      setState({ status: "ready", data: payload, error: null });
    } catch (error) {
      if (error?.name === "AbortError") return;
      setState((current) => ({
        status: current.data?.member ? "stale" : navigator.onLine === false ? "offline" : "error",
        data: current.data,
        error,
      }));
    }
  }, [user]);

  useEffect(() => {
    document.body.classList.add("eternua-dashboard-active");
    document.body.classList.add("eternua-temple-home-active");
    document.body.classList.toggle("eternua-guest-home-active", !user);
    document.body.classList.toggle("eternua-member-home-active", Boolean(user));

    return () => {
      document.body.classList.remove("eternua-dashboard-active");
      document.body.classList.remove("eternua-temple-home-active");
      document.body.classList.remove("eternua-guest-home-active");
      document.body.classList.remove("eternua-member-home-active");
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setState({ status: "idle", data: EMPTY_DASHBOARD, error: null });
      return undefined;
    }
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh, user]);

  useEffect(() => {
    if (!user) return undefined;
    const onOnline = () => refresh();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [refresh, user]);

  const data = state.data || EMPTY_DASHBOARD;
  const greetingName = data.member?.first_name || user?.first_name || user?.username || "";
  const personalizationLabel = data.preferences?.personalization_enabled === false
    ? t("memberHome.personalizationOff", "Personnalisation désactivée")
    : t("memberHome.personalizationOn", "Personnalisation maîtrisée");
  const missionCount = Number(data.progress?.active_missions_count);
  const activeMissionsLabel = Number.isFinite(missionCount) ? String(missionCount) : "—";
  const nextAgendaPriority = (data.priorities?.results || []).find(
    (item) => item?.universe === "Agenda" && item?.date
  );
  const nextAppointmentLabel = formatPrivateAppointment(nextAgendaPriority?.date, lang, t);
  const progressPercent = Number(data.progress?.progress_percent);
  const journeyProgressLabel = Number.isFinite(progressPercent)
    ? `${Math.max(0, Math.min(100, progressPercent))} %`
    : "—";
  const securityLabel = user?.access?.mfa_enabled
    ? t("memberHome.protected", "Compte protégé")
    : t("memberHome.strengthen", "À renforcer");

  const savePreferences = async (draft) => {
    setSavingSettings(true);
    try {
      await updateHomePreferences(draft);
      setSettingsOpen(false);
      await refresh();
    } finally {
      setSavingSettings(false);
    }
  };

  const resetPreferences = async () => {
    setSavingSettings(true);
    try {
      await resetHomePersonalization();
      setSettingsOpen(false);
      await refresh();
    } finally {
      setSavingSettings(false);
    }
  };

  const saveRecommendation = async (item) => {
    setBusyRecommendation(item.id);
    try {
      await setFavorite(item.id, true);
      setState((current) => ({
        ...current,
        data: {
          ...current.data,
          recommendations: {
            ...current.data.recommendations,
            results: current.data.recommendations.results.map((row) => row.id === item.id ? { ...row, favorite: true } : row),
          },
        },
      }));
    } finally {
      setBusyRecommendation(null);
    }
  };

  const feedbackRecommendation = async (item, action) => {
    setBusyRecommendation(item.id);
    try {
      await sendHomeRecommendationFeedback(item.id, action);
      setState((current) => ({
        ...current,
        data: {
          ...current.data,
          recommendations: {
            ...current.data.recommendations,
            count: Math.max(0, Number(current.data.recommendations.count || 0) - 1),
            results: current.data.recommendations.results.filter((row) => row.id !== item.id),
          },
        },
      }));
    } finally {
      setBusyRecommendation(null);
    }
  };

  const loadMore = async () => {
    const page = data.recommendations?.next;
    if (!page || loadingMore) return;
    setLoadingMore(true);
    try {
      const payload = await loadHomeDashboard({ page });
      setState((current) => ({
        ...current,
        data: {
          ...current.data,
          recommendations: {
            ...payload.recommendations,
            results: [
              ...current.data.recommendations.results,
              ...payload.recommendations.results.filter((row) => !current.data.recommendations.results.some((existing) => existing.id === row.id)),
            ],
          },
        },
      }));
    } finally {
      setLoadingMore(false);
    }
  };

  const visitUniverse = async (item) => {
    try {
      await recordHomeUniverseActivity(item.key, "visit");
    } finally {
      navigate(item.route);
    }
  };

  const postponeUniverse = async (item) => {
    await recordHomeUniverseActivity(item.key, "later");
    setState((current) => ({
      ...current,
      data: {
        ...current.data,
        discovery: {
          ...current.data.discovery,
          results: current.data.discovery.results.filter((row) => row.key !== item.key),
        },
      },
    }));
  };

  const shortcutOptions = useMemo(() => data.available_shortcuts || [], [data.available_shortcuts]);
  const universeOptions = useMemo(() => data.discovery?.all || [], [data.discovery]);

  if (authLoading) return <DashboardSkeleton />;
  if (!user) return <GuestHomePage />;
  if (["idle", "loading"].includes(state.status)) return <DashboardSkeleton />;
  if (["error", "offline"].includes(state.status) && !data.member) {
    return <StatusPanel offline={state.status === "offline"} message={state.error?.message || t("memberHome.retryLater", "Réessaie dans un instant.")} onRetry={() => refresh()} />;
  }

  return (
    <main className="home-dashboard">
      <div className="home-ambient home-ambient--one" aria-hidden="true" />
      <div className="home-ambient home-ambient--two" aria-hidden="true" />
      <section className="home-hero" aria-labelledby="home-main-title">
        <div className="home-hero__topline">
          <span className="home-hero__brand"><EternuaIcon name="infinity" />{" "}{<LocalizedText source={t("memberHome.private", "Eternua · Espace privé")} />}</span>
        </div>
        <div className="home-hero__body">
          <div className="home-hero__copy">
            <p className="home-eyebrow">{<LocalizedText source={t("memberHome.hello", "Bonjour")} />} {greetingName}</p>
            <h1 id="home-main-title">{<LocalizedText source={t("memberHome.headlineStart", "Que veux-tu faire")} />}{" "}<em>{<LocalizedText source={t("memberHome.headlineEnd", "aujourd’hui ?")} />}</em></h1>
            <HomeTrustWelcome embedded />
          </div>
          <ConciergeEternua isLoggedIn variant="hero" />
          <div className="home-hero__privacy">
            <span aria-hidden="true">◇</span>
            <span><strong>{<LocalizedText source={personalizationLabel} />}</strong>{" "}{<LocalizedText source={t("memberHome.sensitive", "· aucune donnée sensible exposée")} />}</span>
          </div>
        </div>
        <aside className="home-hero__status" aria-label={t("memberHome.privateAria", "Repères de ton espace privé")}>
          <div className="home-hero__orb" aria-hidden="true"><EternuaIcon name="infinity" /></div>
          <button type="button" className="home-settings-button" onClick={() => setSettingsOpen(true)}>
            <span aria-hidden="true">⊙</span>{<LocalizedText source={t("memberHome.manage", "Gérer mon accueil")} />}</button>
          <div className="home-hero__status-grid">
            <Link className="home-hero__status-card home-hero__status-card--missions" to="/mes-eternua/missions" aria-label={`${t("memberHome.activeMissions", "Missions actives")} : ${activeMissionsLabel}`}>
              <EternuaIcon name="gesture" /><span><small>{<LocalizedText source={t("memberHome.activeMissions", "Missions actives")} />}</small><strong>{activeMissionsLabel}</strong></span>
            </Link>
            <Link className="home-hero__status-card home-hero__status-card--agenda" to="/mes-eternua/agenda" aria-label={`${t("memberHome.appointment", "Prochain rendez-vous")} : ${nextAppointmentLabel}`}>
              <EternuaIcon name="calendar" /><span><small>{<LocalizedText source={t("memberHome.appointment", "Prochain rendez-vous")} />}</small><strong>{nextAppointmentLabel}</strong></span>
            </Link>
            <Link className="home-hero__status-card home-hero__status-card--journey" to="/mon-parcours" aria-label={`${t("memberHome.journey", "Parcours")} : ${journeyProgressLabel}`}>
              <EternuaIcon name="share" /><span><small>{<LocalizedText source={t("memberHome.journey", "Parcours")} />}</small><strong>{journeyProgressLabel}</strong></span>
            </Link>
            <Link className="home-hero__status-card home-hero__status-card--security" to="/mes-eternua/protection" aria-label={`${t("memberHome.security", "Sécurité")} : ${securityLabel}`}>
              <EternuaIcon name="trust" /><span><small>{<LocalizedText source={t("memberHome.security", "Sécurité")} />}</small><strong>{<LocalizedText source={securityLabel} />}</strong></span>
            </Link>
          </div>
        </aside>
      </section>

      <ConciergeTodayActions priorities={data.priorities?.results || []} />
      <EternuaMissionTeaser />
      <EternuaOrientation compact />

      {state.status === "stale" ? (
        <div className="home-network-banner" role="status">{<LocalizedText source={t("memberHome.stale", "Les dernières données chargées restent affichées.")} />}{" "}<button type="button" onClick={() => refresh()}>{<LocalizedText source={t("memberHome.retry", "Réessayer")} />}</button></div>
      ) : null}
      {(data.unavailable_sections || []).length ? (
        <div className="home-network-banner home-network-banner--partial" role="status">{<LocalizedText source={t("memberHome.partial", "Certaines parties de l'accueil sont temporairement indisponibles. Les informations accessibles restent affichées.")} />}</div>
      ) : null}

      <PrioritySection priorities={data.priorities} />
      <ShortcutsSection shortcuts={data.shortcuts || []} />
      <LifeToolsLinks />
      <RecommendationsSection
        recommendations={data.recommendations}
        preferences={data.preferences}
        onOpenSettings={() => setSettingsOpen(true)}
        onSave={saveRecommendation}
        onFeedback={feedbackRecommendation}
        onLoadMore={loadMore}
        busyId={busyRecommendation}
        loadingMore={loadingMore}
      />
      <ProgressSection progress={data.progress} walletState={walletState} />
      <DiscoverySection
        discovery={data.discovery}
        onVisit={visitUniverse}
        onLater={postponeUniverse}
        onOpenAll={() => setAllUniversesOpen(true)}
      />

      {settingsOpen ? (
        <PreferencesDialog
          preferences={data.preferences}
          shortcutOptions={shortcutOptions}
          universeOptions={universeOptions}
          busy={savingSettings}
          onClose={() => setSettingsOpen(false)}
          onSave={savePreferences}
          onReset={resetPreferences}
        />
      ) : null}
      {allUniversesOpen ? (
        <AllUniversesDialog universes={universeOptions} onClose={() => setAllUniversesOpen(false)} onVisit={visitUniverse} />
      ) : null}
    </main>
  );
}
